<template>
  <div class="bg-white w-100pc h-100pc">
    <v-container
      v-if="loaded"
      class="mxw-800 mx-auto py-12"
    >
      <div
        ref="progressBar"
        :aria-label="progressBarAriaLabel"
        class="px-4 focus-invisible"
        role="region"
        tabindex="0"
      >
        <div aria-hidden="true">
          <v-progress-linear
            v-model="progress"
            :indeterminate="!loaded"
            class="focus-invisible mb-4"
            color="primary"
          />
        </div>
      </div>

      <div class="px-4">
        <QuestionSet
          v-model="form"
          @back="backFromQuestion($event)"
          @change:attachments="loadAttachments"
          @next="forwardFromQuestion($event)"
          :answer-validation-complete="stableValidationComplete"
          :attachment-owner-id="form.id"
          :attachment-owner-type="'Form'"
          :attachments="attachments"
          :processing="processing"
          :questions="validPublishedQuestions"
          :readonly="form.locked"
          :schema="formSchema.definition"
          :section="section"
          :transition-name="transitionName"
          key-name="question"
        />
      </div>
    </v-container>
  </div>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import QuestionSet from '@/shared/components/form/QuestionSet.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import useQuestionable from '@/shared/composables/useQuestionable';
import useRouterHelper from '@/shared/composables/useRouterHelper';
import useStepper from '@/shared/composables/useStepper';

const { api } = useApi();
const eventBus = useEventBus();
const route = useRoute();
const router = useRouter();
const { updateQuery } = useRouterHelper();
const store = useStore();
const $bct = inject('translator');

const attachments = ref([]);
const form = ref(null);
const processing = ref(false);
const progressBar = ref(null);

const formSchemaId = computed(() => route.params?.schemaId);

const formSchema = computed(() => store.state.schemas[formSchemaId.value]);

const {
  answerValidationComplete,
  questionsLoaded,
  validateAnswers,
  validPublishedQuestions,
  loadQuestions,
} = useQuestionable({
  ownerDataTypeRef: ref('Schema'),
  ownerIdRef: formSchemaId,
  syncedObjectRef: form,
  skipValidationDuringProcessing: ref(true),
  // Prevent validation on data changes (handled on question navigation instead)
});

const stableValidationComplete = computed(() => {
  return questionsLoaded.value && answerValidationComplete.value && !processing.value;
});

const { progress, section, stepBack, stepCount, stepForward, stepTotal, transitionName } =
  useStepper({
    processing,
    route,
    updateQuery,
  });

const loaded = computed(() => {
  return (
    form.value &&
    formSchema.value &&
    questionsLoaded.value &&
    validPublishedQuestions.value.length > 0
  );
});

const progressBarAriaLabel = computed(() => {
  return `${$bct('Progress indicator - question')} ${stepCount.value}, ${Math.round(Number(progress.value) || 0)}%`;
});

watch(
  () => loaded.value,
  async (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      if (route.query.step) stepCount.value = parseInt(route.query.step, 10);
      else stepCount.value = 0;

      if (!stepTotal.value) await validateAnswers();

      recalculateStepTotal();

      if (route.query.section) {
        section.value = route.query.section;
        progressBar.value?.focus();
        recalculateProgress();
      } else {
        start();
      }
    }
  },
  { immediate: true },
);

watch(
  () => questionsLoaded.value,
  (newVal) => {
    if (newVal) recalculateStepTotal();
  },
);

watch(
  () => route.query.section,
  async (newVal) => {
    const targetSection = newVal || 'question-0';

    // Skip if section is already set to avoid double updates from useStepper
    if (section.value === targetSection && !processing.value) return;

    // Set processing state during navigation
    processing.value = true;

    stepCount.value = parseInt(route.query.step, 10);

    if (!stepTotal.value) await validateAnswers();

    recalculateStepTotal();

    // Only update section when everything is ready
    section.value = targetSection;
    if (newVal) {
      progressBar.value?.focus();
    }
    recalculateProgress();
    processing.value = false;
  },
);

onMounted(() => {
  store.dispatch('identify', {
    api,
    async success() {
      if (store.state.is_anonymous) {
        await router.replace({
          name: 'Signup',
          query: { redirect_url: route.fullPath },
        });
        return;
      }
      await load();
    },
    async failure() {
      await router.replace({
        name: 'Signup',
        query: { mode: 'login', redirect_url: route.fullPath },
      });
    },
  });
});

async function load() {
  await loadForm();
  if (!form.value) return;
  await loadQuestions();
  void loadAttachments();
}

async function loadForm() {
  try {
    const { data } = await api.parent.form.get(route.params.formId);
    form.value = data;
  } catch (error) {
    await router.replace({ name: 'Dashboard' });
  }
}

async function loadAttachments() {
  const params = {
    owner_type: 'Form',
    owner_id: form.value.id,
  };
  const response = await api.member.attachment.index(params);
  if (response?.status !== 200) return;
  attachments.value = response.data;
}

async function saveForm() {
  const { data } = await api.parent.form.update(form.value.id, form.value);
  form.value = data;
}

async function backFromQuestion(index) {
  processing.value = true;
  if (!form.value.locked) {
    await saveForm();
    await validateAnswers();
  }
  recalculateStepTotal();

  if (index - 1 < 0) {
    await router.push({ name: route.query.backTo });
  } else {
    stepBack(`question-${index - 1}`);
  }
}

async function finish() {
  processing.value = true;
  if (!form.value.locked) {
    form.value.submitted = true;
    await api.parent.form.update(form.value.id, form.value);
    eventBus.chime('Form submitted');
  }

  if (formSchema.value?.meta?.submitted_redirect_url) {
    window.location.replace(formSchema.value.meta.submitted_redirect_url);
    return;
  }
  await router.push({ name: route.query.backTo });
}

async function forwardFromQuestion(index) {
  processing.value = true;
  if (!form.value.locked) {
    await saveForm();
    await validateAnswers();
  }
  recalculateStepTotal();

  if (index + 1 >= validPublishedQuestions.value.length) {
    void finish();
  } else {
    stepForward(`question-${index + 1}`);
  }
}

function recalculateProgress() {
  progress.value = (stepCount.value / stepTotal.value) * 100;
}

function recalculateStepTotal() {
  stepTotal.value = validPublishedQuestions.value.length;
  recalculateProgress();
}

function start() {
  processing.value = true;
  if (validPublishedQuestions.value.length > 0) {
    stepForward('question-0');
  } else {
    void finish();
  }
}
</script>
